// ============================================================
// THE B-SCOPE (plan modes-rework, stage 6; the auspex and rail before
// it): the Squad lane's instrument for a brother in Squad Mode, a radar's
// B-scope filling the lane as his life signs do in Solo.
//
//   ACROSS - bearing, -pi..pi: up the map straight up the middle (markers
//            carry no facing for a person since v8.29.0), right of it to
//            the right, behind him at either edge
//   DOWN   - distance, 0 m at the top to the support range at the bottom;
//            a brother beyond it sits on the bottom edge, marked so
//
// Range marks every 10 m. One BEAT (6 s; 3 s for the brother you act as):
// the sweep runs down the scope and lights each sigil as it passes (its
// `delay`, a fraction of the beat). Brothers it cannot place (off the map,
// or on no stage here) are named under it.
//
// Pure. Zero React dependency.
// ============================================================
import type { Frame } from './calloutLayout';

/** A brother as the scope places him (`killTeam/around`). */
export interface Brother {
  id: string;
  name: string;
  /** Metres to him; null when either of them is off the map. */
  metres: number | null;
  /** His bearing from this brother, radians (0 = up the map, clockwise); null off the map. */
  bearing: number | null;
}

interface Blip {
  id: string;
  x: number;
  y: number;
  /** When the sweep reaches him, as a fraction of the beat. */
  delay: number;
  /** Past the support range: on the bottom edge. */
  beyond: boolean;
}

export interface BScope {
  box: { x0: number; x1: number; y0: number; y1: number };
  blips: Blip[];
  /** Range marks, every 10 m. */
  marks: { y: number; m: number }[];
  /** Brothers it cannot place: named under it. */
  absent: string[];
  /** Seconds a beat. */
  beat: number;
}

/** The sweep runs down in the first 90% of the beat; the rest is the pause before the next. */
const SWEEP = 0.9;
/** A margin inside the lane, as a fraction of its width (sides) and height (top, and room for the names under it). */
const SIDE = 0.08;
const TOP = 0.04;
const BOTTOM = 0.08;

const wrap = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));

function boxOf(lane: Frame): BScope['box'] {
  return {
    x0: lane.x + lane.w * SIDE,
    x1: lane.x + lane.w * (1 - SIDE),
    y0: lane.y + lane.h * TOP,
    y1: lane.y + lane.h * (1 - BOTTOM),
  };
}

function blipsOf(brothers: readonly Brother[], box: BScope['box'], rangeM: number): Blip[] {
  const mid = (box.x0 + box.x1) / 2;
  const half = (box.x1 - box.x0) / 2;
  return brothers.flatMap((b) => {
    if (b.metres === null || b.bearing === null) return [];
    const k = Math.min(1, b.metres / rangeM);
    // On the very same spot there is no bearing (atan2 of nothing is pi, a corner): he is on the middle line.
    const across = b.metres > 0.01 ? wrap(b.bearing) / Math.PI : 0;
    return [
      {
        id: b.id,
        x: mid + half * across,
        y: box.y0 + (box.y1 - box.y0) * k,
        delay: SWEEP * k,
        beyond: b.metres > rangeM,
      },
    ];
  });
}

const marksOf = (box: BScope['box'], rangeM: number): BScope['marks'] =>
  Array.from({ length: Math.floor(rangeM / 10) + 1 }, (_, i) => ({
    y: box.y0 + ((box.y1 - box.y0) * i * 10) / rangeM,
    m: i * 10,
  }));

interface BScopeInput {
  lane: Frame;
  brothers: readonly Brother[];
  rangeM: number;
  /** The brother you act as: his beat is twice as quick. */
  acting: boolean;
}

/** The B-scope in its lane. */
export function bscopeOf(a: BScopeInput): BScope {
  const box = boxOf(a.lane);
  return {
    box,
    blips: blipsOf(a.brothers, box, a.rangeM),
    marks: marksOf(box, a.rangeM),
    absent: a.brothers.filter((b) => b.metres === null).map((b) => b.name),
    beat: a.acting ? 3 : 6,
  };
}
